import React, { useState, useEffect } from "react";
import {
    StyledTableCell,
    TableCellHeaderContent,
    TableContainer,
    StyledTable,
} from "./Styled";
import { Table, TableBody, TableHead, TableRow, Checkbox } from "@mui/material";
import { ColumnAttribute } from "../../../configs/productOrder";
import { color } from "../../styles";

const STICKY_COL_WIDTH = 140;

interface TableProps {
    itemList: any[];
    RowElement: any;
    TableColumns: ColumnAttribute[];
    extraRowProps?: any;
    containerStyle?: any;
    selectable?: boolean;
    onSelectionChange?: (selectedIds: any[]) => void;
}

const CustomTable = ({
    itemList,
    RowElement,
    TableColumns,
    extraRowProps,
    containerStyle,
    selectable = false,
    onSelectionChange,
}: TableProps) => {
    const [selected, setSelected] = useState<any[]>([]);

    useEffect(() => {
        if (onSelectionChange) {
            onSelectionChange(selected);
        }
    }, [selected, onSelectionChange]);

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            setSelected(itemList);
            return;
        }
        setSelected([]);
    };

    const handleClick = (item: any) => {
        const selectedIndex = selected.indexOf(item);
        let newSelected: any[] = [];

        if (selectedIndex === -1) {
            newSelected = [...selected, item];
        } else {
            newSelected = selected.filter((_, index) => index !== selectedIndex);
        }

        setSelected(newSelected);
    };

    const isSelected = (item: any) => selected.indexOf(item) !== -1;

    return (
        <TableContainer style={containerStyle}>
            <StyledTable stickyHeader data-testid="list-view-table">
                <TableHead>
                    <TableRow>
                        {selectable && (
                            <StyledTableCell padding="checkbox">
                                <Checkbox
                                    indeterminate={selected.length > 0 && selected.length < itemList.length}
                                    checked={itemList.length > 0 && selected.length === itemList.length}
                                    onChange={handleSelectAllClick}
                                />
                            </StyledTableCell>
                        )}
                        {(() => {
                            let stickyLeft = 0;
                            return TableColumns.map((column: ColumnAttribute) => {
                                const currentLeft = stickyLeft;
                                if (column.sticky) stickyLeft += STICKY_COL_WIDTH;
                                return (
                                    <StyledTableCell
                                        align="left"
                                        key={column.text}
                                        sx={{
                                            ...(column.width ? { minWidth: column.width, width: column.width } : {}),
                                            ...column.sx,
                                            ...(column.sticky ? {
                                                position: 'sticky',
                                                left: currentLeft,
                                                zIndex: 5,
                                                backgroundColor: color.secondary.LIGHT_GREY,
                                            } : {}),
                                        }}
                                    >
                                        <TableCellHeaderContent>
                                            {column.text}
                                            {column.icon}
                                        </TableCellHeaderContent>
                                    </StyledTableCell>
                                );
                            });
                        })()}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {itemList.map((item: any, idx: number) => {
                        const isItemSelected = isSelected(item);
                        return (
                            <RowElement
                                key={
                                    item.rowKey || (item.line_item_id
                                        ? `${item.item_type ? item.item_type : 'item'}:${item.line_item_id}`
                                        : item.client_uid
                                        ? `${item.item_type ? item.item_type : 'item'}:${item.client_uid}`
                                        : item.item_type
                                        ? `${item.item_type}:${item.id}`
                                        : String(item.id))
                                }
                                item={item}
                                extraRowProps={{
                                    ...extraRowProps,
                                    selectable,
                                    isSelected: isItemSelected,
                                    onSelect: () => handleClick(item),
                                }}
                            />
                        );
                    })}
                </TableBody>
            </StyledTable>
        </TableContainer>
    );
};

export default CustomTable;
